// Sub-Step 3.3 Phase 2 — SignInModal-Komponente.
//
// Pre-Redirect-Modal das User informiert dass sie zu portal.scrolly.com
// gesendet werden (Domain-Wechsel). User entscheidet bewusst:
//   - Anmelden  → auth.signinRedirect mit return_to=window.location.href
//                  Keycloak macht echten Login-Flow via scrolly-public-Client
//   - Abbrechen → Modal schließt, anonyme Session bleibt
//
// Pre-Verify Q-3.3-4 — bewusste UX-Klarheit über Direct-Redirect.
//
// Accessibility:
//   - role="dialog" + aria-modal="true"
//   - aria-labelledby auf Title
//   - ESC schließt Modal
//   - Backdrop-Klick schließt Modal
//   - Focus auf Primary-Button beim Open
//   - Inert-Background (best effort — wir setzen tabIndex=-1 nicht überall)
//
// Style folgt public/-Stack Design-Tokens (tokens.ts):
//   - PINK #FF3366 für Primary-Button
//   - Display-Serif (FONT_DISPLAY) für Headline
//   - DM Sans für Body + Buttons
//   - Card-Radius 14px, Modal-Radius 18px (etwas größer)
//
// useEffect-Audit (Phase-1-Lesson): zwei useEffects (ESC-Handler +
// focus-Mount). Beide haben enge Dependencies und cleanup-Functions.
// Keine API-Calls hier — kein Loop-Risk.

import { useEffect, useRef, type ReactNode } from "react";
import { PINK, FONT_DISPLAY, FONT_UI, RADIUS, SHADOW } from "../../lib/tokens";

interface SignInModalProps {
  /** Modal-Sichtbarkeit. Steuern via local state in Parent (z.B. SideActions). */
  open: boolean;
  /** Callback wenn User Modal schließt (Abbrechen-Button, ESC, Backdrop). */
  onClose: () => void;
  /** Callback wenn User "Anmelden" klickt. Soll signIn(returnTo) triggern. */
  onConfirmSignIn: () => void;
  /**
   * Optional: Custom-Headline anstelle des Defaults
   * "Anmelden bei portal.scrolly.com".
   */
  headline?: string;
  /** Optional: Custom-Subtext anstelle des Standard-Hinweises. */
  subtext?: string | ReactNode;
}

export function SignInModal({
  open,
  onClose,
  onConfirmSignIn,
  headline = "Anmelden bei portal.scrolly.com",
  subtext = "Du wirst zur Anmeldung weitergeleitet. Nach dem Login kommst du automatisch zurück.",
}: SignInModalProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);
  const titleId = "signin-modal-title";

  // ESC-Handler — schließt Modal. Cleanup beim Unmount oder open=false.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus-Trap-Lite — focus auf Primary-Button beim Open für Tastatur-User.
  useEffect(() => {
    if (!open) return;
    // Defer focus until next tick so the modal is rendered.
    const timer = window.setTimeout(() => {
      primaryRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(e) => {
        // Backdrop-Click schließt — child-clicks bubbeln aber stopPropagation
        // im inneren Container blockt sie.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        // Plan-N2 Block 1.d (2026-05-25 Bene): SignInModal MUSS über
        // CoverSection/StartScreen liegen. Beide sind position:absolute
        // INNERHALB des Scroll-Containers mit z-[1002] — innerhalb des
        // ViewerChrome-Stacking-Contexts (z-index:100) gewinnt 1002 > 1000,
        // also "leakte" das Modal links/rechts neben dem PhoneFrame raus
        // statt davor zu liegen (Bene-Befund "Ebenen-Problem — Fenster
        // hinter dem Scrolly"). 10000 bleibt sicher über CoverSection
        // (1002), StartScreen (1002), packages/CompanyLogo (2000) und dem
        // historischen app/-Stack-Wert (9999). Z.modal in z-index.ts ist
        // mitgepflegt.
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 440,
          width: "100%",
          background: "#ffffff",
          borderRadius: 18,
          padding: "32px 32px 28px",
          boxShadow: SHADOW.cardHover,
          fontFamily: FONT_UI,
          color: "#111111",
        }}
      >
        <h2
          id={titleId}
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 26,
            fontWeight: 500,
            margin: 0,
            letterSpacing: "-0.5px",
            lineHeight: 1.15,
            color: "#111111",
          }}
        >
          {headline}
        </h2>
        <p
          style={{
            marginTop: 14,
            marginBottom: 28,
            fontSize: 15,
            lineHeight: 1.5,
            color: "#5a5a55",
          }}
        >
          {subtext}
        </p>
        <div
          style={{
            display: "flex",
            flexDirection: "row-reverse",
            gap: 10,
            alignItems: "center",
          }}
        >
          <button
            ref={primaryRef}
            type="button"
            onClick={() => {
              // Modal-Close NICHT vorher rufen — der Browser navigiert sowieso
              // weg via signinRedirect. Reduziert Flash-of-Modal-Close vor Nav.
              onConfirmSignIn();
            }}
            style={{
              background: PINK,
              color: "#ffffff",
              border: "none",
              borderRadius: RADIUS.button,
              padding: "11px 22px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            Anmelden
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              color: "#5a5a55",
              border: "none",
              borderRadius: RADIUS.button,
              padding: "11px 18px",
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
